/**
 * Casaio Shipping Insurance / Gift Wrap — 2026-09-24, revision 2.
 * Replace assets/cart-gift-wrap.js; do not load both implementations.
 * Honors Casaio's data-preselect setting and the visitor's explicit opt-out.
 */
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';
import { sectionRenderer } from '@theme/section-renderer';

const WRAPPER = '.cart-gift-wrap';
const CHECKBOX = '.cart-gift-wrap__checkbox';
const SOURCE = 'cart-gift-wrap';
const preferences = new Map();
const disabledControls = new Map();
let busy = false;
let queued = false;
let scheduled = false;
let lastContext = null;
let autoAddBlocked = false;

function getContext() {
  const wrapper = document.querySelector(`cart-drawer-component ${WRAPPER}`)
    || document.querySelector(WRAPPER);
  const component = wrapper?.closest('cart-items-component');
  if (wrapper?.dataset.variantId && component?.dataset.sectionId) {
    lastContext = {
      variantId: wrapper.dataset.variantId,
      sectionId: component.dataset.sectionId,
      preselect: wrapper.dataset.preselect === 'true',
    };
  }
  return lastContext;
}

function preference(variantId, value) {
  const key = `mooths:shipping-insurance:${variantId}`;
  if (value !== undefined) {
    preferences.set(key, value);
    try { sessionStorage.setItem(key, value); } catch { /* Memory fallback. */ }
  }
  if (preferences.has(key)) return preferences.get(key);
  try { return sessionStorage.getItem(key); } catch { return null; }
}

function cartUrl(action) {
  const root = window.Shopify?.routes?.root || '/';
  const fallback = `${root.endsWith('/') ? root : `${root}/`}cart${action ? `/${action}` : ''}.js`;
  return new URL(window.Theme?.routes?.[action ? `cart_${action}_url` : 'cart_url'] || fallback,
    window.location.origin);
}

// cart_url can point to the HTML cart page; reads always use the Ajax endpoint.
function readCart() {
  const root = window.Shopify?.routes?.root || '/';
  const url = new URL(`${root.endsWith('/') ? root : `${root}/`}cart.js`, window.location.origin);
  url.searchParams.set('_insurance', Date.now().toString());
  return request(url);
}

async function request(url, body) {
  const response = await fetch(String(url), {
    method: body === undefined ? 'GET' : 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const data = await response.json();
  if (!response.ok || data.errors || Number(data.status) >= 400) {
    throw new Error('Shipping insurance request failed');
  }
  return data;
}

function sectionIds(context) {
  return [...new Set([context.sectionId, ...Array.from(
    document.querySelectorAll('cart-items-component'), (el) => el.dataset.sectionId,
  )].filter(Boolean))].slice(0, 5);
}

function lockControls() {
  document.querySelectorAll(`${CHECKBOX}, cart-items-component [name="checkout"]`).forEach((el) => {
    if (!disabledControls.has(el)) disabledControls.set(el, el.disabled);
    el.disabled = true;
  });
}

function unlockControls() {
  for (const [el, wasDisabled] of disabledControls) el.disabled = wasDisabled;
  disabledControls.clear();
}

function reflectCart(cart, context) {
  const selected = cart.items.some((item) => String(item.variant_id) === context.variantId);
  document.querySelectorAll(WRAPPER).forEach((wrapper) => {
    if (wrapper.dataset.variantId !== context.variantId) return;
    const checkbox = wrapper.querySelector(CHECKBOX);
    if (checkbox) checkbox.checked = selected;
  });
}

function showError() {
  const wrapper = document.querySelector(WRAPPER);
  if (!wrapper || wrapper.querySelector('[data-insurance-error]')) return;
  const message = document.createElement('p');
  message.dataset.insuranceError = '';
  message.setAttribute('role', 'alert');
  message.textContent = 'Shipping insurance could not be updated. Please check your cart and try again.';
  wrapper.append(message);
}

async function publishCart(cart, context, sections = {}) {
  // Obtain missing HTML without repeating a cart mutation, even after a null section.
  const rendered = { ...sections };
  for (const id of sectionIds(context)) {
    if (!rendered[id]) rendered[id] = await sectionRenderer.getSectionHTML(id, false);
  }
  // Use Casaio's server-rendered counter rather than the quantity just added.
  const html = rendered[context.sectionId];
  const countText = new DOMParser().parseFromString(html, 'text/html')
    .querySelector('[ref="cartItemCount"]')?.textContent;
  const visibleCount = Number.parseInt(countText, 10);
  document.dispatchEvent(new CartUpdateEvent(cart, context.sectionId, {
    source: SOURCE,
    itemCount: Number.isFinite(visibleCount) ? visibleCount : cart.item_count,
    sections: rendered,
    // Casaio's add and update events share the same name. Do not reopen its drawer.
    deferDrawerOpen: true,
  }));
  reflectCart(cart, context);
  lockControls();
}

function scheduleReconcile() {
  queued = true;
  if (busy || scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => requestAnimationFrame(() => {
    scheduled = false;
    void reconcile();
  }));
}

async function reconcile() {
  if (busy || !queued) return;
  queued = false;
  const context = getContext();
  if (!context) return;
  busy = true;
  lockControls();
  try {
    let cart = await readCart();
    const insurance = cart.items.filter((item) => String(item.variant_id) === context.variantId);
    const hasProducts = cart.items.some((item) => String(item.variant_id) !== context.variantId);
    const choice = preference(context.variantId);
    const drawerOpen = !!document.querySelector('cart-drawer-component dialog[open]');
    const wantsInsurance = hasProducts && choice !== 'off'
      && (insurance.length > 0 || (!autoAddBlocked && (choice === 'on' || (context.preselect && drawerOpen))));
    const body = {
      sections: sectionIds(context),
      sections_url: window.location.pathname,
    };
    let sections;
    let changed = false;

    if (wantsInsurance && insurance.length === 0) {
      const url = cartUrl('add');
      // Exemption used by the installed Kaching Popup 469 interceptor itself.
      // Only this optional line bypasses the add-to-cart popup; normal adds are untouched.
      url.searchParams.set('kaching_popup', 'true');
      const added = await request(url, { ...body, items: [{ id: context.variantId, quantity: 1 }] });
      changed = true;
      sections = added.sections;
      // add.js returns added lines, not the complete cart.
      cart = await readCart();
      if (!cart.items.some((item) => String(item.variant_id) === context.variantId)) {
        // Other apps also write cart attributes during the first add. Recheck once
        // after those requests settle, then SET quantity to 1 rather than incrementing.
        // This repair is only reached after a confirmed successful add, never a timeout.
        await new Promise((resolve) => setTimeout(resolve, 250));
        cart = await readCart();
        sections = {};
        if (preference(context.variantId) !== 'off'
          && cart.items.some((item) => String(item.variant_id) !== context.variantId)
          && !cart.items.some((item) => String(item.variant_id) === context.variantId)) {
          cart = await request(cartUrl('update'), { ...body, updates: { [context.variantId]: 1 } });
          sections = cart.sections;
        }
      }
    } else {
      // Use stable line keys, never a position that an upsell might have changed.
      const updates = {};
      insurance.forEach((item, index) => {
        const quantity = wantsInsurance && index === 0 ? 1 : 0;
        if (item.quantity !== quantity) updates[item.key] = quantity;
      });
      if (Object.keys(updates).length) {
        cart = await request(cartUrl('update'), { ...body, updates });
        changed = true;
        sections = cart.sections;
      }
    }
    if (changed) await publishCart(cart, context, queued ? {} : sections);
    else reflectCart(cart, context);
  } catch {
    // A response can fail AFTER Shopify changed the cart. Never blindly add again.
    autoAddBlocked = true;
    queued = false;
    try { await publishCart(await readCart(), context); } catch { /* Keep a visible error. */ }
    showError();
  } finally {
    unlockControls();
    busy = false;
    if (queued) scheduleReconcile();
  }
}

document.addEventListener('change', (event) => {
  const checkbox = event.target;
  if (!checkbox?.matches?.(CHECKBOX)) return;
  const wrapper = checkbox.closest(WRAPPER);
  if (!wrapper?.dataset.variantId) return;
  getContext();
  // Save BEFORE the removal: rerendering/opening the drawer must not undo a refusal.
  preference(wrapper.dataset.variantId, checkbox.checked ? 'on' : 'off');
  autoAddBlocked = false;
  scheduleReconcile();
});

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.data?.source === SOURCE) return;
  scheduleReconcile();
});

document.addEventListener(DialogOpenEvent.eventName, (event) => {
  if (event.target?.closest?.('cart-drawer-component')
    || document.querySelector('cart-drawer-component dialog[open]')) scheduleReconcile();
});

// Casaio can open an EMPTY drawer before asynchronously inserting its cart section.
// Watch for the insurance block itself as well as the dialog's open attribute.
// Changes inside the block (including error messages) do not retrigger this observer.
const observer = new MutationObserver((mutations) => {
  if (mutations.some((m) => m.type === 'attributes' && m.attributeName === 'open'
    && m.target.open && m.target.closest('cart-drawer-component'))) {
    scheduleReconcile();
    return;
  }
  // Our own section publication also inserts this block. It must not start a retry loop.
  if (busy || !document.querySelector('cart-drawer-component dialog[open]')) return;
  const insuranceInserted = mutations.some((m) => m.type === 'childList'
    && [...m.addedNodes].some((node) => node.nodeType === Node.ELEMENT_NODE
      && (node.matches(WRAPPER) || node.querySelector(WRAPPER))));
  if (insuranceInserted) scheduleReconcile();
});
observer.observe(document.documentElement, {
  subtree: true, childList: true, attributes: true, attributeFilter: ['open'],
});

// Cover a restored page or a drawer already open when this module loads.
getContext();
if (document.querySelector('cart-drawer-component dialog[open]')) scheduleReconcile();
